import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ProfileWidget from "./ProfileWidget";
import { APP_ENV } from "../../config";
import { logout } from "../../utils";
import { ModalConfirm } from "tamtam-components";
import _ from "../../i18n";

const Profile = ({ contact, scrollToTop }) => {
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
  const auth = useSelector(state => state.auth);
  const { token, jwtToken, user, scope, goPeopleUserId, goPeopleCommunityId } =
    auth;
  const language = useSelector(state => state.params.language);
  const [isOpen, setIsOpen] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const selectedOrganizationId = auth?.navCommunity?.id;
  const userId = auth?.user?.id;

  useEffect(
    () => {
      if (scope?.includes("_ls")) {
        setShouldRender(false);
        setIsOpen(true);
      } else {
        setShouldRender(true);
      }
      const script = document.createElement("script");
      let userSelected = contact ?? user;

      if (userSelected) {
        // No cache-buster here on purpose. The CDN serves this fixed-name entry
        // with Cache-Control: no-cache, so the browser revalidates it by ETag on
        // every open: a new deploy is picked up immediately, an unchanged one
        // costs a 304 instead of re-downloading the whole bundle. Appending a
        // timestamp would make every URL unique again and defeat both the
        // browser cache and the CloudFront edge.
        script.src = `https://s3.tamtam.pro/cdn/profile/gopeople/${
          APP_ENV === "rc2"
            ? "staging"
            : APP_ENV === "v2" || APP_ENV === "production"
              ? "production"
              : APP_ENV
        }/static/js/profileWidget.js`;
        script.async = true;
        script.onload = () => {
          setIsWidgetLoaded(true);
        };

        document.body.appendChild(script);
      }

      return () => {
        if (userSelected) {
          document.body.removeChild(script);
        }
      };
    },
    [user, contact],
  );

  if (isWidgetLoaded) {
    let userSelected = contact ?? user;
    // Own-profile mode only: use the logged-in user's goPeople ID.
    const widgetUserId = goPeopleUserId;

    // The widget gates its user query on the id it receives at mount time and
    // never remounts on its own, so mounting it before the id is known leaves
    // it stuck on its loading mask (blank page). Wait for the id instead, and
    // key the widget on it so a late or changed id remounts it.
    if (!contact && !widgetUserId) {
      return null;
    }

    // Two lists open this widget with two different item shapes. The contacts
    // list passes a goPeople contact, which carries uuid. The OECCBB adhesions
    // list passes a TTP user (Settings/tabs/ManageMembership), which only has
    // the numeric TTP user id in id. The widget resolves either one, but it has
    // to be told which it received: with both props empty it leaves single
    // contact mode and falls back to own profile with no user id, and stays on
    // its loading mask for good.
    const contactEditUuid = contact?.uuid ?? null;
    const contactTtpUserId = contactEditUuid ? null : (contact?.id ?? null);
    const contactKey = contactEditUuid || contactTtpUserId;

    return (
      <>
        {!shouldRender ? (
          <ModalConfirm
            type="logout"
            isOpen={isOpen}
            onCancel={() => {
              setIsOpen(false);
              window.history.back();
            }}
            onConfirm={() => logout()}
            inProcess={false}
            title={_("confirm_logout_title")}
            text={_("confirm_logout_text")}
            labelNo={_("cancel")}
            labelYes={_("connection")}
            labelError={_("error")}
          />
        ) : (
          <>
            <link
              href={`https://s3.tamtam.pro/cdn/profile/gopeople/${
                APP_ENV === "rc2"
                  ? "staging"
                  : APP_ENV === "v2" || APP_ENV === "production"
                    ? "production"
                    : APP_ENV
              }/static/css/profileWidget.css`}
              rel="stylesheet"
            />
            <ProfileWidget
              key={contact ? contactKey : widgetUserId}
              token={jwtToken}
              lng={language}
              isContact={!!contact}
              onScrollToTop={scrollToTop}
              selectedOrganization={selectedOrganizationId}
              updatedByUser={userId}
              // Own-profile mode: edit the logged-in user.
              user_uuid={contact ? null : widgetUserId}
              // Contact mode: drives the widget's isSingleContactMode so it
              // renders ContactProfileView and saves via updateCommunityContact.
              community_id={contact ? goPeopleCommunityId : null}
              contact_edit_uuid={contactEditUuid}
              contact_ttp_user_edit_id={contactTtpUserId}
            /></>)}
      </>
    );
  }
  return null;
};

export default Profile;
